import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

/** No token for a tap target; 44px is the minimum comfortable one. */
export const MIN_TAP_TARGET = '44px';

/**
 * Ribbons' panel is transparent and full-width, so this pins the body to the right edge and
 * carries the shadow. The wrapper sets the width inline from the buy-box column's left edge;
 * below 768px `min-width` clamps that to the whole screen, the same way the delivery and
 * product options drawers do. A viewport query on purpose, since a fixed drawer's space is the
 * viewport. Applied by the wrapper, not the content root, so the content stories lay out in flow.
 */
export const panel = css`
  position: fixed;
  top: 0;
  right: 0;
  min-width: 100%;
  height: 100%;
  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.2);

  @media (min-width: 768px) {
    min-width: 0;
  }
`;

/** Carries its own surface, or the page's off-white ground shows through. */
export const section = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(${themeVariables.colors.surface.default});
  font-family: var(${themeVariables.typography.body.fontFamily});
  color: var(${themeVariables.colors.content.default});
`;

/** Outside the scroll container, so the close control stays reachable however far down. */
export const header = css`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-start;
  padding: var(${themeVariables.spacing.md});
`;

/** Buttons do not inherit the document font, so the body typography is restated. */
export const closeButton = css`
  display: inline-flex;
  align-items: center;
  gap: var(${themeVariables.spacing.xxs});
  min-height: ${MIN_TAP_TARGET};
  padding: 0;
  border: 0;
  background: none;
  font-family: var(${themeVariables.typography.body.fontFamily});
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});
  letter-spacing: var(${themeVariables.typography.body.letterSpacing});
  color: var(${themeVariables.colors.content.interactive});
  cursor: pointer;
`;

export const reviewContent = css`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 var(${themeVariables.spacing.md}) var(${themeVariables.spacing.md});
`;

/** The average, the histogram and the attribution, spaced as the design's top section. */
export const summary = css`
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.md});
`;

export const divider = css`
  width: 100%;
  height: var(${themeVariables.strokeWidth});
  margin: 0;
  border: 0;
  background-color: var(${themeVariables.colors.stroke.default});
`;

export const headingRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${themeVariables.spacing.sm});
`;

export const heading = css`
  margin: 0;
  font-family: var(${themeVariables.typography.heading.fontFamily});
  font-weight: var(${themeVariables.typography.heading.fontWeight});
  font-size: var(${themeVariables.typography.heading.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.heading.sizes.sm.lineHeight});
  letter-spacing: var(${themeVariables.typography.heading.letterSpacing});
  color: var(${themeVariables.colors.content.default});
`;

export const content = css`
  container-type: inline-size;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  overflow-y: auto;
  row-gap: var(${themeVariables.spacing.xl});
  padding: 0 var(${themeVariables.spacing.md}) var(${themeVariables.spacing.md});
`;

/** The star and "4.8/5", centered, in heading type. */
export const average = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(${themeVariables.spacing.xxs});
  margin: 0;
  font-family: var(${themeVariables.typography.heading.fontFamily});
  font-weight: var(${themeVariables.typography.heading.fontWeight});
  font-size: var(${themeVariables.typography.heading.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.heading.sizes.md.lineHeight});
  letter-spacing: var(${themeVariables.typography.heading.letterSpacing});
  color: var(${themeVariables.colors.content.default});
`;

export const list = css`
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.md});
  margin: 0;
  padding: 0;
  list-style: none;
`;

/** Reserves a card's height while a page is in flight, so paging keeps the scroll position. */
export const skeletonItem = css`
  height: 160px;
  border-radius: var(${themeVariables.borderRadius.md.mobile});
  background-color: var(${themeVariables.colors.surface.disabled});
`;

export const message = css`
  margin: 0;
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});
  color: var(${themeVariables.colors.content.secondary});
`;
